import React, { useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Alert } from 'react-native';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from '../../../store';
import {
  generateQuiz,
  selectCurrentQuiz,
} from '../../../store/slices/aiTutoringSlice';
import { selectCurrentUser } from '../../../store/slices/authSlice';

interface QuizGenerationCardProps {
  onGenerateQuiz: (topicId: string) => void;
  loading: boolean;
}

const QuizGenerationCard: React.FC<QuizGenerationCardProps> = ({
  onGenerateQuiz,
  loading,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const user = useSelector(selectCurrentUser);
  const currentQuiz = useSelector(selectCurrentQuiz);

  const [selectedTopic, setSelectedTopic] = useState<string>(
    'brewing-fundamentals'
  );
  const [selectedDifficulty, setSelectedDifficulty] = useState<number>(50);
  const [questionCount, setQuestionCount] = useState<number>(10);
  const [adaptiveMode, setAdaptiveMode] = useState<boolean>(true);

  const topics = [
    {
      id: 'brewing-fundamentals',
      name: 'Brewing Fundamentals',
      icon: 'local-cafe',
    },
    { id: 'grind-techniques', name: 'Grind Techniques', icon: 'settings' },
    { id: 'extraction-science', name: 'Extraction Science', icon: 'science' },
    { id: 'water-quality', name: 'Water Quality', icon: 'water-drop' },
    { id: 'equipment-mastery', name: 'Equipment Mastery', icon: 'build' },
    { id: 'flavor-profiling', name: 'Flavor Profiling', icon: 'palette' },
  ];

  const questionCounts = [5, 10, 15, 20];
  const difficultyLevels = [
    { value: 25, label: 'Beginner', color: '#4CAF50' },
    { value: 50, label: 'Intermediate', color: '#FF9800' },
    { value: 75, label: 'Advanced', color: '#F44336' },
    { value: 100, label: 'Expert', color: '#9C27B0' },
  ];

  const handleGenerateQuiz = async () => {
    if (!user?.id) {
      Alert.alert('Error', 'Please log in to generate quizzes');
      return;
    }

    try {
      await dispatch(
        generateQuiz({
          userId: user.id,
          topicId: selectedTopic,
          skillGaps: ['brewing-consistency', 'extraction-timing'], // This would come from user's skill assessment
          targetDifficulty: selectedDifficulty,
          questionCount,
          adaptiveEnabled: adaptiveMode,
        })
      );

      Alert.alert(
        'Quiz Generated!',
        'Your personalized quiz is ready. Would you like to start now?',
        [
          { text: 'Later', style: 'cancel' },
          { text: 'Start Quiz', onPress: () => onGenerateQuiz(selectedTopic) },
        ]
      );
    } catch (error) {
      Alert.alert('Error', 'Failed to generate quiz. Please try again.');
    }
  };

  const getDifficultyInfo = (value: number) => {
    return (
      difficultyLevels.find(level => level.value === value) ||
      difficultyLevels[1]
    );
  };

  const renderTopicSelector = () => (
    <View style={styles.selectorSection}>
      <Text style={styles.selectorTitle}>Topic</Text>
      <View style={styles.topicsGrid}>
        {topics.map(topic => (
          <TouchableOpacity
            key={topic.id}
            style={[
              styles.topicCard,
              selectedTopic === topic.id && styles.selectedTopicCard,
            ]}
            onPress={() => setSelectedTopic(topic.id)}
          >
            <Icon
              name={topic.icon}
              size={20}
              color={selectedTopic === topic.id ? '#007AFF' : '#666'}
            />
            <Text
              style={[
                styles.topicText,
                selectedTopic === topic.id && styles.selectedTopicText,
              ]}
            >
              {topic.name}
            </Text>
          </TouchableOpacity>
        ))}
      </View>
    </View>
  );

  const renderDifficultySelector = () => {
    const currentDifficulty = getDifficultyInfo(selectedDifficulty);

    return (
      <View style={styles.selectorSection}>
        <Text style={styles.selectorTitle}>Difficulty Level</Text>
        <View style={styles.difficultyContainer}>
          <View style={styles.difficultySlider}>
            {difficultyLevels.map(level => (
              <TouchableOpacity
                key={level.value}
                style={[
                  styles.difficultyOption,
                  selectedDifficulty === level.value && {
                    backgroundColor: `${level.color}15`,
                    borderColor: level.color,
                  },
                ]}
                onPress={() => setSelectedDifficulty(level.value)}
              >
                <Text
                  style={[
                    styles.difficultyText,
                    selectedDifficulty === level.value && {
                      color: level.color,
                    },
                  ]}
                >
                  {level.label}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
          <View style={styles.difficultyInfo}>
            <Icon name='info' size={16} color={currentDifficulty.color} />
            <Text style={styles.difficultyDescription}>
              {selectedDifficulty}% difficulty - Questions will be tailored to{' '}
              {currentDifficulty.label.toLowerCase()} level
            </Text>
          </View>
        </View>
      </View>
    );
  };

  const renderQuestionCountSelector = () => (
    <View style={styles.selectorSection}>
      <Text style={styles.selectorTitle}>Number of Questions</Text>
      <View style={styles.countSelector}>
        {questionCounts.map(count => (
          <TouchableOpacity
            key={count}
            style={[
              styles.countOption,
              questionCount === count && styles.selectedCountOption,
            ]}
            onPress={() => setQuestionCount(count)}
          >
            <Text
              style={[
                styles.countText,
                questionCount === count && styles.selectedCountText,
              ]}
            >
              {count}
            </Text>
          </TouchableOpacity>
        ))}
      </View>
      <Text style={styles.estimatedTime}>
        Estimated time: {Math.ceil(questionCount * 1.5)} minutes
      </Text>
    </View>
  );

  const renderAdaptiveToggle = () => (
    <View style={styles.selectorSection}>
      <View style={styles.adaptiveHeader}>
        <View style={styles.adaptiveInfo}>
          <Text style={styles.selectorTitle}>Adaptive Mode</Text>
          <Text style={styles.adaptiveDescription}>
            AI adjusts difficulty based on your performance
          </Text>
        </View>
        <TouchableOpacity
          style={[
            styles.adaptiveToggle,
            adaptiveMode && styles.adaptiveToggleActive,
          ]}
          onPress={() => setAdaptiveMode(!adaptiveMode)}
        >
          <View
            style={[
              styles.adaptiveToggleThumb,
              adaptiveMode && styles.adaptiveToggleThumbActive,
            ]}
          />
        </TouchableOpacity>
      </View>

      {adaptiveMode && (
        <View style={styles.adaptiveFeatures}>
          <View style={styles.featureItem}>
            <Icon name='psychology' size={16} color='#4CAF50' />
            <Text style={styles.featureText}>
              Dynamic difficulty adjustment
            </Text>
          </View>
          <View style={styles.featureItem}>
            <Icon name='lightbulb' size={16} color='#FF9800' />
            <Text style={styles.featureText}>
              Intelligent hints and explanations
            </Text>
          </View>
          <View style={styles.featureItem}>
            <Icon name='trending-up' size={16} color='#2196F3' />
            <Text style={styles.featureText}>
              Personalized learning insights
            </Text>
          </View>
        </View>
      )}
    </View>
  );

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.title}>AI Quiz Generator</Text>
        <Text style={styles.subtitle}>
          Create personalized quizzes based on your learning gaps
        </Text>
      </View>

      {currentQuiz ? (
        <View style={styles.existingQuizSection}>
          <View style={styles.existingQuizHeader}>
            <Icon name='quiz' size={24} color='#4CAF50' />
            <Text style={styles.existingQuizTitle}>Quiz Ready!</Text>
          </View>
          <Text style={styles.existingQuizDescription}>
            Your {currentQuiz.questions.length}-question quiz on{' '}
            {currentQuiz.topicId} is ready to start.
          </Text>
          <View style={styles.quizStats}>
            <View style={styles.statItem}>
              <Icon name='quiz' size={16} color='#666' />
              <Text style={styles.statText}>
                {currentQuiz.questions.length} questions
              </Text>
            </View>
            <View style={styles.statItem}>
              <Icon name='timer' size={16} color='#666' />
              <Text style={styles.statText}>
                {currentQuiz.estimatedTime} min
              </Text>
            </View>
            <View style={styles.statItem}>
              <Icon name='psychology' size={16} color='#666' />
              <Text style={styles.statText}>
                {currentQuiz.adaptiveLogic.adaptationEnabled
                  ? 'Adaptive'
                  : 'Fixed'}
              </Text>
            </View>
          </View>
          <TouchableOpacity
            style={styles.startQuizButton}
            onPress={() => onGenerateQuiz(currentQuiz.topicId)}
          >
            <Icon name='play-arrow' size={20} color='#fff' />
            <Text style={styles.startQuizButtonText}>Start Quiz</Text>
          </TouchableOpacity>
        </View>
      ) : (
        <View style={styles.generatorSection}>
          {renderTopicSelector()}
          {renderDifficultySelector()}
          {renderQuestionCountSelector()}
          {renderAdaptiveToggle()}

          <TouchableOpacity
            style={[
              styles.generateButton,
              loading && styles.generateButtonDisabled,
            ]}
            onPress={handleGenerateQuiz}
            disabled={loading}
          >
            <Icon
              name={loading ? 'hourglass-empty' : 'auto-awesome'}
              size={20}
              color='#fff'
            />
            <Text style={styles.generateButtonText}>
              {loading ? 'Generating Quiz...' : 'Generate Quiz'}
            </Text>
          </TouchableOpacity>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  header: {
    marginBottom: 20,
  },
  title: {
    fontSize: 20,
    fontWeight: '700',
    color: '#1a1a1a',
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 14,
    color: '#666',
  },
  existingQuizSection: {
    alignItems: 'center',
  },
  existingQuizHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
    gap: 8,
  },
  existingQuizTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#4CAF50',
  },
  existingQuizDescription: {
    fontSize: 14,
    color: '#666',
    textAlign: 'center',
    marginBottom: 16,
  },
  quizStats: {
    flexDirection: 'row',
    gap: 16,
    marginBottom: 20,
  },
  statItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  statText: {
    fontSize: 12,
    color: '#666',
  },
  startQuizButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#4CAF50',
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 24,
    gap: 8,
  },
  startQuizButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#fff',
  },
  generatorSection: {
    gap: 20,
  },
  selectorSection: {},
  selectorTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1a1a1a',
    marginBottom: 12,
  },
  topicsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  topicCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f8f9fa',
    borderRadius: 8,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderWidth: 2,
    borderColor: 'transparent',
    gap: 6,
  },
  selectedTopicCard: {
    backgroundColor: '#e3f2fd',
    borderColor: '#007AFF',
  },
  topicText: {
    fontSize: 12,
    fontWeight: '500',
    color: '#666',
  },
  selectedTopicText: {
    color: '#007AFF',
  },
  difficultyContainer: {},
  difficultySlider: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 8,
  },
  difficultyOption: {
    flex: 1,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 6,
    backgroundColor: '#f8f9fa',
    alignItems: 'center',
    borderWidth: 2,
    borderColor: 'transparent',
  },
  difficultyText: {
    fontSize: 12,
    fontWeight: '500',
    color: '#666',
  },
  difficultyInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  difficultyDescription: {
    fontSize: 12,
    color: '#666',
    flex: 1,
  },
  countSelector: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 8,
  },
  countOption: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 6,
    backgroundColor: '#f8f9fa',
    borderWidth: 2,
    borderColor: 'transparent',
  },
  selectedCountOption: {
    backgroundColor: '#e3f2fd',
    borderColor: '#007AFF',
  },
  countText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#666',
  },
  selectedCountText: {
    color: '#007AFF',
  },
  estimatedTime: {
    fontSize: 12,
    color: '#666',
  },
  adaptiveHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  adaptiveInfo: {
    flex: 1,
  },
  adaptiveDescription: {
    fontSize: 12,
    color: '#666',
    marginTop: 2,
  },
  adaptiveToggle: {
    width: 44,
    height: 24,
    borderRadius: 12,
    backgroundColor: '#e1e5e9',
    justifyContent: 'center',
    paddingHorizontal: 2,
  },
  adaptiveToggleActive: {
    backgroundColor: '#007AFF',
  },
  adaptiveToggleThumb: {
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: '#fff',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.2,
    shadowRadius: 2,
    elevation: 2,
  },
  adaptiveToggleThumbActive: {
    alignSelf: 'flex-end',
  },
  adaptiveFeatures: {
    gap: 6,
  },
  featureItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  featureText: {
    fontSize: 12,
    color: '#666',
  },
  generateButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#007AFF',
    borderRadius: 8,
    paddingVertical: 14,
    gap: 8,
  },
  generateButtonDisabled: {
    backgroundColor: '#ccc',
  },
  generateButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#fff',
  },
});

export default QuizGenerationCard;
